import { OperatorsEnum } from '@celito.clients/enums';
import {
  IFilterView,
  RulesComponentListViewFilter,
} from 'libs/shared/src/lib/rules-component/types/rules-component.types';

/**
 * Extracts filters for object_id__s and moduleFilterForObjectDefinition from the default filter view for biz_admin__a module
 * and transforms them to name__s and moduleName respectively for use in the filter panel.
 * This is used to filter the object dropdown in the filter panel based on the current filter view.
 */
export const getObjectFiltersForBizAdmin = (
  currentModule: string | undefined,
  filterViews: IFilterView[] | undefined
): RulesComponentListViewFilter[] | undefined => {
  // Only apply for biz_admin__a module
  if (currentModule !== 'biz_admin__a' || !filterViews) {
    return undefined;
  }

  // Get the default filter view
  const defaultFilterView = filterViews.find((view) => view.isDefault === true);

  if (!defaultFilterView?.filtersToBeApplied) {
    return undefined;
  }

  // Deep clone and filter to only keep conditions where attribute is 'object_id__s' or 'moduleFilterForObjectDefinition'
  const objectFilters = defaultFilterView.filtersToBeApplied
    .map((filter) => {
      if (!filter.conditions) return null;

      const filteredConditions: any = {};

      Object.keys(filter.conditions).forEach((outerKey) => {
        const outerConditions = filter.conditions?.[outerKey];
        if (!Array.isArray(outerConditions)) return;

        const filteredOuterConditions = outerConditions
          .map((innerConditionGroup) => {
            const filteredInnerGroup: any = {};

            Object.keys(innerConditionGroup).forEach((innerKey) => {
              const conditions = innerConditionGroup[innerKey];
              if (!Array.isArray(conditions)) return;

              const filteredConditions = conditions
                .filter(
                  (condition) =>
                    condition.attribute === 'object_id__s' ||
                    condition.moduleFilterForObjectDefinition
                )
                .map((condition) => {
                  if (condition.moduleFilterForObjectDefinition) {
                    // Transform moduleFilterForObjectDefinition to moduleName with equals operator
                    return {
                      attribute: 'moduleName',
                      operator: OperatorsEnum.IN,
                      value: condition.moduleFilterForObjectDefinition,
                    };
                  } else {
                    // Transform object_id__s to name__s
                    return {
                      ...condition,
                      attribute: 'name__s',
                    };
                  }
                });

              if (filteredConditions.length > 0) {
                filteredInnerGroup[innerKey] = filteredConditions;
              }
            });

            return Object.keys(filteredInnerGroup).length > 0
              ? filteredInnerGroup
              : null;
          })
          .filter((group) => group !== null);

        if (filteredOuterConditions.length > 0) {
          filteredConditions[outerKey] = filteredOuterConditions;
        }
      });

      return Object.keys(filteredConditions).length > 0
        ? { conditions: filteredConditions }
        : null;
    })
    .filter((filter) => filter !== null) as RulesComponentListViewFilter[];

  return objectFilters.length > 0 ? objectFilters : undefined;
};
